import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { AlertCircle, CheckCircle2, ShieldCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { authService } from '@/services/auth';
import { useAuthStore } from '@/stores/authStore';
import { getApiError } from '@/services/api';
import { OAuthButton, OAuthDivider } from './OAuthButton';

interface LoginForm {
  email: string;
  password: string;
}

interface MfaForm {
  code: string;
}

export function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const justRegistered = Boolean((location.state as { registered?: boolean } | null)?.registered);
  const [searchParams] = useSearchParams();
  const { setAuth, setMfaRequired, tempToken, requiresMfa, clearMfaRequired } = useAuthStore();
  const [error, setError] = useState<string | null>(null);

  // Handle OAuth error from redirect
  useEffect(() => {
    const oauthError = searchParams.get('error');
    if (oauthError) {
      setError(decodeURIComponent(oauthError));
    }
  }, [searchParams]);

  const handleOAuthLogin = (provider: 'google' | 'microsoft') => {
    window.location.href = authService.getOAuthUrl(provider);
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginForm>();

  const {
    register: registerMfa,
    handleSubmit: handleMfaSubmit,
    formState: { errors: mfaErrors },
  } = useForm<MfaForm>();

  const loginMutation = useMutation({
    mutationFn: authService.login,
    onSuccess: (data) => {
      if (data.requiresMfa && data.tempToken) {
        setMfaRequired(data.tempToken);
      } else if (data.accessToken && data.refreshToken && data.user) {
        setAuth(data.user, data.accessToken, data.refreshToken);
        navigate('/');
      }
    },
    onError: (err) => {
      setError(getApiError(err).message);
    },
  });

  const mfaMutation = useMutation({
    mutationFn: authService.validateMfa,
    onSuccess: (data) => {
      if (data.accessToken && data.refreshToken && data.user) {
        setAuth(data.user, data.accessToken, data.refreshToken);
        navigate('/');
      }
    },
    onError: (err) => {
      setError(getApiError(err).message);
    },
  });

  const onLogin = (data: LoginForm) => {
    setError(null);
    loginMutation.mutate(data);
  };

  const onMfaSubmit = (data: MfaForm) => {
    if (!tempToken) return;
    setError(null);
    mfaMutation.mutate({ tempToken, code: data.code });
  };

  const errorAlert = error && (
    <div className="alert-danger mb-6" role="alert">
      <AlertCircle className="h-5 w-5 shrink-0" />
      <span>{error}</span>
    </div>
  );

  if (requiresMfa) {
    return (
      <div>
        <div className="mb-8">
          <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-xl bg-primary-50">
            <ShieldCheck className="h-6 w-6 text-primary-600" />
          </div>
          <h1 className="text-2xl font-semibold tracking-tight text-gray-900">{t('auth.mfa.title')}</h1>
          <p className="mt-2 text-sm text-gray-500">{t('auth.mfaSubtitle')}</p>
        </div>

        {errorAlert}

        <form onSubmit={handleMfaSubmit(onMfaSubmit)} className="space-y-5" noValidate>
          <div>
            <label htmlFor="code" className="label">
              {t('auth.mfa.enterCode')}
            </label>
            <input
              type="text"
              id="code"
              inputMode="numeric"
              autoComplete="one-time-code"
              autoFocus
              maxLength={6}
              dir="ltr"
              className={`${mfaErrors.code ? 'input-error' : 'input'} py-3 text-center text-2xl font-semibold tracking-[0.5em]`}
              placeholder="000000"
              {...registerMfa('code', {
                required: t('auth.fieldRequired'),
                pattern: { value: /^\d{6}$/, message: t('auth.mfaSubtitle') },
              })}
            />
            {mfaErrors.code && <p className="field-error">{mfaErrors.code.message}</p>}
          </div>

          <button type="submit" disabled={mfaMutation.isPending} className="btn-primary btn-lg w-full">
            {mfaMutation.isPending ? t('common.loading') : t('auth.mfa.verify')}
          </button>

          <button type="button" onClick={clearMfaRequired} className="btn-ghost w-full">
            {t('common.cancel')}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-semibold tracking-tight text-gray-900">{t('auth.welcomeBack')}</h1>
        <p className="mt-2 text-sm text-gray-500">{t('auth.loginSubtitle')}</p>
      </div>

      {justRegistered && !error && (
        <div className="alert-success mb-6" role="status">
          <CheckCircle2 className="h-5 w-5 shrink-0" />
          <span>{t('auth.registered')}</span>
        </div>
      )}
      {errorAlert}

      <form onSubmit={handleSubmit(onLogin)} className="space-y-5" noValidate>
        <div>
          <label htmlFor="email" className="label">
            {t('auth.email')}
          </label>
          <input
            type="email"
            id="email"
            autoComplete="email"
            dir="ltr"
            className={errors.email ? 'input-error' : 'input'}
            placeholder="email@example.com"
            {...register('email', {
              required: t('auth.errors.emailRequired'),
              pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                message: t('validation.emailInvalid'),
              },
            })}
          />
          {errors.email && <p className="field-error">{errors.email.message}</p>}
        </div>

        <div>
          <label htmlFor="password" className="label">
            {t('auth.password')}
          </label>
          <input
            type="password"
            id="password"
            autoComplete="current-password"
            className={errors.password ? 'input-error' : 'input'}
            placeholder="••••••••"
            {...register('password', {
              required: t('auth.errors.passwordRequired'),
            })}
          />
          {errors.password && <p className="field-error">{errors.password.message}</p>}
        </div>

        <button type="submit" disabled={loginMutation.isPending} className="btn-primary btn-lg w-full">
          {loginMutation.isPending ? t('common.loading') : t('auth.login')}
        </button>
      </form>

      <OAuthDivider />

      <div className="grid gap-3 sm:grid-cols-2">
        <OAuthButton
          provider="google"
          onClick={() => handleOAuthLogin('google')}
          disabled={loginMutation.isPending}
        />
        <OAuthButton
          provider="microsoft"
          onClick={() => handleOAuthLogin('microsoft')}
          disabled={loginMutation.isPending}
        />
      </div>

      <p className="mt-8 text-center text-sm text-gray-500">
        {t('auth.noAccount')}{' '}
        <Link to="/register" className="link">
          {t('auth.register')}
        </Link>
      </p>
    </div>
  );
}
